import React, { useState, useEffect } from 'react';
import { Container, Spinner, Modal, Button, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getDivisions, addDivision, getDivisionHistory } from '../../services/division-service';
import ResponsiveTable from '../Grid/ResponsiveTable';
import InputBox from '../common/InputBox';
import { formatDate } from '../../helpers/dateUtils';

const EMPTY_FORM = { divisionCode: '', divisionDesc: '' };

const DivisionMaster = () => {
  const [loading, setLoading]       = useState(false);
  const [data, setData]             = useState([]);
  const [showModal, setShowModal]   = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [isEdit, setIsEdit]         = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getDivisions();
      if (res.statusCode === '0000') setData(res.data || []);
    } catch { toast.error('Failed to load divisions'); }
    finally { setLoading(false); }
  };

  const handleAdd = () => {
    setForm(EMPTY_FORM);
    setIsEdit(false);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setForm({ divisionCode: item.divisionCode, divisionDesc: item.divisionDesc || '' });
    setIsEdit(true);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.divisionCode) { toast.error('Division Code is required'); return; }
    setLoading(true);
    try {
      const res = await addDivision(form);
      if (res.statusCode === '0000') {
        toast.success(res.statusMsg);
        setShowModal(false);
        loadData();
      } else {
        toast.error(res.statusMsg || 'Failed');
      }
    } catch { toast.error('Failed to save division'); }
    finally { setLoading(false); }
  };

  const viewHistory = async (item) => {
    setLoading(true);
    try {
      const res = await getDivisionHistory(item.divisionCode);
      setHistoryData(res.data || []);
      setShowHistory(true);
    } catch { toast.error('Failed to load history'); }
    finally { setLoading(false); }
  };

  const headers = [
    { displayName: 'Division Code', columnName: 'divisionCode', dataType: 'text', width: 160, displayIndication: true },
    { displayName: 'Division Name', columnName: 'divisionDesc', dataType: 'text', width: 250, displayIndication: true },
    { displayName: 'Modified By',   columnName: 'lastModifiedBy', dataType: 'text', width: 200, displayIndication: true },
    { displayName: 'Modified On',   columnName: 'lastModifiedOn', dataType: 'date', width: 180, displayIndication: true, formatter: formatDate },
  ];

  const icons = (item) => [
    { type: 'button', text: 'Edit',    click: () => handleEdit(item) },
    { type: 'button', text: 'History', click: () => viewHistory(item) },
  ];

  const historyHeaders = [
    { displayName: 'Division Code', columnName: 'divisionCode', dataType: 'text', width: 160, displayIndication: true },
    { displayName: 'Division Name', columnName: 'divisionDesc', dataType: 'text', width: 250, displayIndication: true },
    { displayName: 'Modified By',   columnName: 'lastModifiedBy', dataType: 'text', width: 200, displayIndication: true },
    { displayName: 'Modified On',   columnName: 'lastModifiedOn', dataType: 'date', width: 180, displayIndication: true, formatter: formatDate },
  ];

  return (
    <>
      {loading && <div className="spinner-box"><Spinner animation="border" className="spinner" /></div>}
      <Container bsPrefix="main-body">
        <div className="page-title">Division Master</div>
        <div className="page-sub-title">Manage IOD business divisions (used in user assignments and email triggers)</div>
        <ResponsiveTable
          headers={headers} data={data} icons={icons}
          dataType="Divisions" button="Division" handleAdd={handleAdd}
          sessionName="Division Master" noCheckbox={true} pageSize={10}
          enableDownload={true} forceWriteAccess={true}
        />
      </Container>

      <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? 'Edit Division' : 'Add Division'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={12}>
              <InputBox type="text" label="Division Code" name="divisionCode" value={form.divisionCode}
                onChange={(e) => setForm((p) => ({ ...p, divisionCode: e.target.value }))}
                disabled={isEdit} required={true} />
            </Col>
            <Col md={12}>
              <InputBox type="text" label="Division Name" name="divisionDesc" value={form.divisionDesc}
                onChange={(e) => setForm((p) => ({ ...p, divisionDesc: e.target.value }))} />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button className="add-btn" onClick={handleSubmit}>{isEdit ? 'Update' : 'Submit'}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showHistory} onHide={() => setShowHistory(false)} size="lg" backdrop="static">
        <Modal.Header closeButton><Modal.Title>Division History</Modal.Title></Modal.Header>
        <Modal.Body>
          <ResponsiveTable headers={historyHeaders} data={historyData} dataType="DivisionHistory" noCheckbox={true} pageSize={10} />
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowHistory(false)}>Close</Button></Modal.Footer>
      </Modal>
    </>
  );
};

export default DivisionMaster;
